import { Box, Chip, Typography } from "@mui/material";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import { useTranslation } from "react-i18next";
import { Deal, DealType } from "src/business/deal";
import {
  colorBorder,
  colorBorderSuccess,
  colorSurfaceSuccess,
  colorTextMuted,
  colorTextPrimary,
  colorTextSuccess,
  colorChipBackground,
  colorChipText,
  colorChipBorder,
  iconBgInfo,
  iconBgPurple,
  iconColorInfo,
  iconColorPurple,
  radiusChip,
} from "src/design/tokens";

interface OfferRowProps {
  deal: Deal;
  isLast: boolean;
}

/** How many day/time chips fit before the row starts summarising. */
const MAX_SCHEDULE_PARTS = 2;

export const OfferRow = ({ deal, isLast }: OfferRowProps) => {
  const { t } = useTranslation();
  const isEvent = deal.type === DealType.EVENT;

  const schedule = deal.timeRanges
    .slice(0, MAX_SCHEDULE_PARTS)
    .join(" · ");
  const extraRanges = Math.max(deal.timeRanges.length - MAX_SCHEDULE_PARTS, 0);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1.25,
        borderBottom: isLast ? "none" : `1px solid ${colorBorder}`,
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          flexShrink: 0,
          borderRadius: radiusChip,
          backgroundColor: isEvent ? iconBgPurple : iconBgInfo,
          color: isEvent ? iconColorPurple : iconColorInfo,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isEvent ? (
          <EventNoteOutlinedIcon sx={{ fontSize: 18 }} />
        ) : (
          <LocalOfferOutlinedIcon sx={{ fontSize: 18 }} />
        )}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{ fontSize: "14px", fontWeight: 600, color: colorTextPrimary }}
          noWrap
        >
          {deal.content}
        </Typography>
        <Typography sx={{ fontSize: "12px", color: colorTextMuted }} noWrap>
          {[
            isEvent ? t("dashboard.offers.event") : t("dashboard.offers.deal"),
            schedule,
            extraRanges > 0 ? `+${extraRanges}` : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </Typography>
      </Box>

      <Chip
        label={
          deal.active
            ? t("dashboard.offers.active")
            : t("dashboard.offers.inactive")
        }
        size="small"
        sx={{
          flexShrink: 0,
          height: 20,
          fontSize: "11px",
          fontWeight: 600,
          backgroundColor: deal.active ? colorSurfaceSuccess : colorChipBackground,
          color: deal.active ? colorTextSuccess : colorChipText,
          border: `1px solid ${deal.active ? colorBorderSuccess : colorChipBorder}`,
          "& .MuiChip-label": { px: 1 },
        }}
      />
    </Box>
  );
};
